import { useRef, useState } from "react";
import { FiUpload, FiMusic } from "react-icons/fi";
import { useTranslation, Trans } from "react-i18next";
import ComponentCard from "../../../../../shared/ui/common/ComponentCard";
import Button from "../../../../../shared/ui/button/Button";
import { isAiConsentRequired, uploadSessionAudio } from "../../../application/uploadSessionAudio";
import { Info, CheckCircle2 } from "lucide-react";
import { LoadingOverlay } from "../../../../../shared/ui/common/LoadingOverlay";
import { useNavigate } from "react-router";
import {
  MIN_SESSION_AUDIO_MINUTES,
  MIN_SESSION_AUDIO_SECONDS,
  audioDurationOf,
  formatDuration,
} from "../../../domain/audioMinimum";

interface Props {
  /** Where "finish later" goes: the sessions of the record this one belongs to. */
  exitPath: string;
  sessionId: string;
  onExtractedText?: (text: string) => void;
  onContinue: () => void;
  onBack?: () => void;
}

export default function SessionAddUploadAudioForm({
  sessionId,
  exitPath,
  onContinue,
  onBack,
}: Props) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  const validateFile = (f: File) => {
    if (!f.type.startsWith("audio/")) {
      return t("session.audio.errorType");
    }
    return null;
  };

  const handleFile = async (f: File) => {
    const validationError = validateFile(f);
    if (validationError) {
      setError(validationError);
      setFile(null);
      return;
    }

    // Measured here rather than after the upload: a file too short to work
    // from should not cost the therapist a wait and a transfer first. A file
    // the browser cannot measure is accepted — a codec it does not know is not
    // evidence of a short recording.
    const duration = await audioDurationOf(f);
    if (duration !== null && duration < MIN_SESSION_AUDIO_SECONDS) {
      setError(
        t("session.audio.errorTooShort", {
          duration: formatDuration(duration),
          minutes: MIN_SESSION_AUDIO_MINUTES,
        })
      );
      setFile(null);
      return;
    }

    setError(null);
    setFile(f);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFile(droppedFile);
    }
  };

  const handleContinue = async () => {
    if (!file) return onContinue?.();
    setLoading(true);
    setError(null);
    try {
      const contentType = file.type || "audio/webm";

      await uploadSessionAudio(sessionId, file, contentType);

      setIsProcessing(true);
    } catch (err) {
      console.error(err);
      if (isAiConsentRequired(err)) {
        setError(t("patient.portalConsent.aiBlocked"));
      } else {
        setError(t("session.audio.errorUpload"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFinishLater = () => {
    navigate(exitPath);
  };

  if (isProcessing) {
    return (
      <ComponentCard title={t("session.audio.processingTitle", "Audio is being processed")}>
        <div className="flex flex-col items-center justify-center p-8 space-y-6 text-center">
          <div className="w-16 h-16 bg-accent-tint rounded-full flex items-center justify-center mb-2">
            <CheckCircle2 className="w-8 h-8 text-accent-text" />
          </div>

          <h3 className="text-xl font-bold text-gray-900">
            {t("session.audio.processingHeader", "Your audio is processing in the background")}
          </h3>

          <p className="text-gray-600 max-w-md">
            {t(
              "session.audio.processingDescription",
              "We've safely queued your audio. Scribe will transcribe the audio, generate clinical notes, and run the AI analysis. We'll notify you as soon as it's ready!"
            )}
          </p>

          <div className="pt-6 w-full max-w-xs space-y-3">
            <Button
              className="w-full"
              onClick={handleFinishLater}
            >
              {t("session.audio.goToDashboard", "Go to Dashboard")}
            </Button>
          </div>
        </div>
      </ComponentCard>
    );
  }

  return (
    <ComponentCard title={t("session.audio.title")}>
      <div className="space-y-6">
        {/* AI Frame */}
        <div className="ai-spectrum-border mb-6 rounded-xl">
          <div className="flex items-start gap-3 rounded-xl p-4">
            <Info className="mt-0.5 size-4 shrink-0 text-accent-text" />

            <p className="text-sm text-content-secondary leading-snug">
              <Trans
                i18nKey="session.audio.aiInfo"
                components={{
                  1: <strong className="text-accent-text" />,
                  3: <span className="font-medium" />
                }}
              />
            </p>
          </div>
        </div>

        {/* DROP ZONE */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`
            cursor-pointer rounded-2xl border-2 border-dashed p-10
            flex flex-col items-center justify-center text-center
            transition
            ${dragActive
              ? "border-accent bg-accent-tint"
              : "border-gray-300 hover:border-accent"
            }
          `}
        >
          <FiUpload className="text-3xl text-accent-text mb-3" />

          <p className="font-medium">
            {t("session.audio.dragDrop")}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {t("session.audio.browse")}
          </p>
          {/* Said before the file is chosen, not after it is rejected. */}
          <p className="mt-3 max-w-sm text-center text-xs text-content-tertiary">
            {t("session.minAudio.notice", { minutes: MIN_SESSION_AUDIO_MINUTES })}
          </p>

          <input
            ref={inputRef}
            type="file"
            accept="audio/*"
            hidden
            onChange={(e) => {
              const selected = e.target.files?.[0];
              if (selected) handleFile(selected);
            }}
          />
        </div>

        {/* FILE PREVIEW */}
        {file && (
          <div className="flex items-center gap-3 rounded-lg border p-4 bg-gray-50">
            <FiMusic className="text-xl text-accent-text" />
            <div className="flex-1">
              <p className="text-sm font-medium">{file.name}</p>
              <p className="text-xs text-gray-500">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
        )}

        {/* ERROR */}
        {error && (
          <p className="text-sm text-red-600 text-center">{error}</p>
        )}

        {/* ACTIONS */}
        <div className="flex justify-center gap-4 pt-6 border-t">
          <Button variant="outline" onClick={onBack}>
            {t("session.back")}
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!file}
          >
            {t("session.continue")}
          </Button>
        </div>
      </div>

      <LoadingOverlay
        visible={loading}
        message={t("session.audio.processing")}
      />

    </ComponentCard>
  );
}
